import TokenService from './authService/TokenService'
import isOnePlatformEnabled from './isOnePlatformEnabled'
import SentryService from './Sentry/SentryService'
import { getPersonalWorkspace } from './authService/personalService'

function getPathFromURL(url: string) {
  let path
  if (url.startsWith('http')) {
    // full url with protocol
    ;[, path] = url.split('://')
  } else if (url.startsWith('/')) {
    // url with domain but no protocol
    path = url.slice(1)
  } else {
    // path only url
    path = url
  }

  const pathParts = path.split('/')
  if (pathParts[0].includes('.')) pathParts.shift()

  return pathParts.join('/')
}

export function getBasenameFromPath(url?: string) {
  if (url) {
    const [workspaceKey] = getPathFromURL(url).split('/')
    return workspaceKey
  }
  const [, workspaceKey] = window.location.pathname.split('/')
  return workspaceKey
}

export function getLocalWorkspacePath(url: string) {
  const path = getPathFromURL(url)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_workspaceKey, ...pathParts] = path.split('/')
  return `/${pathParts.join('/')}`
}

export function isHome(): boolean {
  const siteKey = getBasenameFromPath()
  return siteKey === 'home'
}

export function getBasename() {
  if (isHome()) {
    return 'home'
  }
  return TokenService.getInstance().getCurrentSiteToken()?.site_key || 'home'
}

/**
 * Prefixes the url with the api domain if OnePlatform is enabled.
 * `/api/setting/list` becomes `domain.tld/api/setting/list`
 * @param url - the url to transform
 */
export const apiUrlTransformer = (url: string): string => {
  // For urls outside One Platform, don't transform the url
  if (!isOnePlatformEnabled) {
    return url
  }
  // Cookies thumbnail urls are handled service-side, so
  // should not be transformed either.
  if (/\/thumbnail-cookie\//.test(url)) {
    return url
  }
  try {
    const currentSite = TokenService.getInstance().getCurrentSiteToken()
    return new URL(url, currentSite?.api_base_url).href
  } catch (error: unknown) {
    SentryService.captureException(error as Error)
  }

  return url
}

/**
 * Prefixes the url with the api domain based on a provided workspace token
 * @param url - the url to transform
 * @param workspaceUrl - token of a specific workspace
 */
export const apiUrlTransformerByWorkspaceToken = (
  url: string,
  workspaceUrl: string
): string => {
  // Cookies thumbnail urls are handled service-side, so
  // should not be transformed either.
  if (/\/thumbnail-cookie\//.test(url)) {
    return url
  }
  try {
    const currentSite = TokenService.getInstance().getTokenFromUrl(workspaceUrl)
    return new URL(url, currentSite?.api_base_url).href
  } catch (error: unknown) {
    SentryService.captureException(error as Error)
  }

  return url
}

/**
 * Prefixes the url with the workspace key if OnePlatform is enabled.
 * `/manage/webinars` becomes `workspace_key/manage/webinars`
 * @param url - the url to transform
 */
export const baseNameUrlTransformer = (url: string): string => {
  if (!isOnePlatformEnabled) {
    return url
  }
  try {
    const basename = TokenService.getInstance().getCurrentSiteToken()?.site_key
    let uri = url
    if (!uri.startsWith('/')) uri = `/${uri}`
    return `/${basename}${uri}`
  } catch (error: unknown) {
    SentryService.captureException(error as Error)
  }
  return url
}

/**
 * Prefixes the url with the api domain for the available
 * personal workspace if OnePlatform is enabled.
 * `/api/setting/list` becomes `personal-domain.tld/api/setting/list`
 * @param url - the url to transform
 */
export const personalApiUrlTransformer = (url: string) => {
  // For urls outside One Platform, don't transform the url
  if (!isOnePlatformEnabled) {
    return url
  }

  try {
    const personalSite = getPersonalWorkspace()
    return new URL(url, personalSite.workspaceData?.api_base_url).href
  } catch (error: unknown) {
    SentryService.captureException(error as Error)
  }

  return url
}
